<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { variantConfig, sizeConfig } from '../ui/Button/variant'

const props = defineProps<{
  class?: HTMLAttributes['class']
  title?: string
  faqs: { question: string; answer: string }[]
  showedCount?: number
}>()

const constructFaqs = computed(() => {
  const constructedFaqs = props.faqs.map((faq, index) => ({
    value: 'faq-' + index,
    title: faq.question,
    content: faq.answer
  }))

  return {
    showed: constructedFaqs.slice(0, props.showedCount || 7),
    hidden: constructedFaqs.slice(props.showedCount || 7)
  }
})

const { public: config } = useRuntimeConfig()
const route = useRoute()

useHead({
  script: [
    {
      key: 'faq-schema',
      type: 'application/ld+json',
      innerHTML: JSON.stringify(
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          '@id': `${config.url}${route.fullPath}#faq`,
          mainEntity: props.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
                .replaceAll(/<[^>]+>/g, ' ')
                .replaceAll(/\s+/g, ' ')
                .trim()
            }
          }))
        },
        null,
        2
      )
    }
  ]
})
</script>

<template>
  <section :class="cn('lg:py-10', props.class)">
    <UiContainer class="py-12 flex flex-col items-center gap-3 lg:gap-10">
      <UiSectionTitle
        class="text-center"
        variant-size="small"
        :title="title || 'Pertanyaan yang Sering Diajukan'"
        sub-title="FAQ"
        sub-title-as="div"
      />

      <div class="order-last w-full lg:w-8/12">
        <UiCollapsibleBase
          :class-trigger="[
            'order-last flex items-center justify-center font-medium gap-1 mx-auto mt-4 border rounded-full',
            variantConfig.primaryOutline,
            sizeConfig.default
          ]"
        >
          <template
            v-if="constructFaqs.hidden.length > 0"
            #trigger="{ isOpen }"
          >
            <span>Lihat Lebih {{ isOpen ? 'Sedikit' : 'Banyak' }}</span>
            <Icon
              name="material-symbols:expand-more"
              :class="{
                'transition-all': true,
                '-rotate-180': isOpen
              }"
              size="24px"
            />
          </template>
          <UiAccordionBase
            class="gap-4"
            :items="constructFaqs.showed"
            :collapsible="true"
            type="multiple"
          />
          <template
            v-if="constructFaqs.hidden.length > 0"
            #content="{ isOpen }"
          >
            <div class="flex flex-col" :class="{ 'mt-4': isOpen }">
              <UiAccordionBase
                class="gap-4"
                :items="constructFaqs.hidden"
                :collapsible="true"
                type="multiple"
              />
            </div>
          </template>
        </UiCollapsibleBase>
      </div>
    </UiContainer>
  </section>
</template>
